/* eslint-disable  react/jsx-props-no-spreading */
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import get from 'lodash.get';
import { Trans } from 'react-i18next';
import { stub as $t } from '@nbcnews/analytics-framework';

import { verticalSlugMap } from 'lib/vertical';
import { getDataActivityMapID } from 'lib/articleUtils';
import { getAmazonProductOffersTrackingObject } from 'lib/commerceUtils';
import {
  MYNEWS_ENABLED,
  ARTICLE_INLINE_NEWSLETTER,
  USER_ACTIONS_BELOW_ARTICLE,
  UNIVERSAL_CHECKOUT_WELLS_FARGO_BANNER,
  SHOW_USER_ACTIONS,
  ARTICLE_TAXONOMY_SECTION_NUMBERING,
} from 'lib/brandFeatures';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { AccountLoginRegistration } from 'components/AccountLoginRegistration';
import {
  isBlogArticle,
  isShellArticle,
  shouldShowAd,
} from 'lib/article';
import { getCookie } from 'lib/cookies';
import { datadogRum } from '@datadog/browser-rum';
import { withAccountWorkflows } from 'lib/Hooks/useAccountWorkflows';
import { logError } from 'lib/datadog';
import { withGateAccess } from 'lib/Hooks/withGateAccess';
import { useMyNewsStore } from 'store';
import { VIEW } from 'lib/view';
import Ad from 'components/Ad';
import { Decibel } from '@nbcnews/decibel';
import { BackToTopButton } from 'components/BackToTopButton';
import { useInView } from 'react-intersection-observer';
import { LiveBlogPosts } from 'components/LiveBlogPosts';
import NewsletterSignupInline from 'components/NewsletterSignup/Inline';
import { RecirculationBacon } from 'components/RecirculationBacon';
import { SocialShareMenu } from 'components/Social/SocialShareMenu';
import MostPopularStoryList from 'components/MostPopularStoryList';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { WellsFargoBanner } from 'components/WellsFargoBanner';
import Related from 'components/Article/Related';
import { FeaturedRecipes } from 'components/Article/FeaturedRecipes';
import { TableOfContents } from 'components/TableOfContents';
import { extractTermPaths } from 'lib/taxonomy';
import { getAuthenticationState, launchAuthenticationModal } from 'lib/authenticationUtils';
import { useFeatureFlagContext, BreakpointContext } from 'lib/ContextTypes';
import { shouldShowBoxesBestsellersBacon, shouldShowProductBacon } from 'lib/article/taxonomyUtils';
import { ExpandableByline } from 'components/ExpandableByline';
import { ExpandedBylineContributors } from '../ExpandedBylineContributors';
import { ArticleFoot } from '../ArticleFoot';
import { getNormalizedArticleBody } from '../articleUtils';
import { getSections } from '../getSections';
import { BodyRightRail } from './BodyRightRail';
import { BodyTaboola } from './BodyTaboola';
import { BodyBottomRecommended } from './BodyBottomRecommended';
import { SocialAndSponsored } from './SocialAndSponsored';
import './styles.themed.scss';
import { StandardByline } from './StandardByline';
import { useSaveArticleHistory } from './hooks/useSaveArticleHistory';
import { useArticleScroll } from './hooks/useArticleScroll';
import { boxflexAdRenderCheck } from './bodyUtils';

const block = 'article-body';

$t('register', 'rmn_amazon_products_known', { allowDuplicate: true });

const {
  news: NEWS,
  today: TODAY,
} = verticalSlugMap;

/**
 * Maps state to props for the ArticleBody component.
 * @param {object} state - The state from the Redux store.
 * @param {object} state.article - The article state.
 * @param {object} state.myNews - The myNews state.
 * @returns {object} The mapped props.
 */
const mapStateToProps = ({ article, myNews }) => ({
  contentId: get(article, 'content[0].id', ''),
  myNews,
});

/**
 * ArticleBody component renders the body of an article.
 * @param {object} props - The properties object.
 * @param {Article} props.article - The article object.
 * @param {string} props.contentId - The content ID.
 * @param {boolean} [props.showCreatedDate=true] - Flag to show the created date.
 * @param {string} props.vertical - The vertical type.
 * @param {boolean} [props.showUserActions=MYNEWS_ENABLED.default] - Flag to show user actions.
 * @param {boolean} [props.showBylineTimestamp=true] - Flag to show byline timestamp.
 * @param {boolean} [props.showInlineByline=true] - Flag to show inline byline.
 * @param {boolean} [props.isLiveBlog=false] - Flag to indicate if it is a live blog.
 * @param {string} [props.bylineTimestampDatePublished=undefined] - The byline timestamp date published.
 * @param {boolean} [props.tableOfContentsEnabled=false] - Flag to enable table of contents.
 * @param {string} [props.backToTopBtnsAlignDesktop='top'] - Alignment of back to top buttons on desktop.
 * @param {string} [props.backToTopBtnsAlignMobile='top'] - Alignment of back to top buttons on mobile.
 * @param {string} props.path - The current path.
 * @param {Function} [props.getRightRailAdConfig=null] - Function to get right rail ad config.
 * @param {boolean} [props.shouldRenderRightRailTaboola=null] - Flag to render right rail Taboola.
 * @param {boolean} [props.isChromeless=false] - Flag to indicate if it is chromeless.
 * @param {string} [props.view=null] - The view type.
 * @param {boolean} [props.isShoppable=false] - Flag to indicate if it is shoppable.
 * @param {boolean} [props.taboolaRecoReelEnabled=false] - Flag to enable Taboola Reco Reel.
 * @param {string} [props.className=null] - Additional class name.
 * @param {boolean} [props.disableGrid=false] - Flag to disable grid.
 * @param {boolean} [props.shouldRenderTaboolaFeed=true] - Flag to render Taboola feed.
 * @param {object} props.gateAccess - The gate access object.
 * @param {Array} [props.mostPopularStoryListItems=[]] - List of most popular story items.
 * @param {Array} [props.relatedArticles=[]] - List of related articles.
 * @param {object} [props.boxBestsellersList] - The box bestsellers bacon curated list data.
 * @param {Function} [props.setIsInlineTableOfContentsInView=()=>{}]
 * @param {object[]} [props.tableOfContents=[]]
 * @param {'standard' | 'expandable' | 'none'} [props.bylineType='standard'] - Type of byline to render.
 */
export function ArticleBody(props) {
  const {
    article,
    contentId,
    showCreatedDate = true,
    vertical,
    showUserActions = MYNEWS_ENABLED.default,
    showBylineTimestamp = true,
    showInlineByline = true,
    isLiveBlog = false,
    bylineTimestampDatePublished = undefined,
    tableOfContentsEnabled = false,
    backToTopBtnsAlignDesktop = 'top',
    backToTopBtnsAlignMobile = 'top',
    path: currentPath,
    getRightRailAdConfig = null,
    shouldRenderRightRailTaboola = null,
    isChromeless = false,
    view = null,
    isShoppable = false,
    path,
    taboolaRecoReelEnabled = false,
    className = null,
    disableGrid = false,
    shouldRenderTaboolaFeed = true,
    gateAccess,
    mostPopularStoryListItems = [],
    relatedArticles = [],
    showDecibelPlayer,
    boxBestsellersList,
    setIsInlineTableOfContentsInView,
    tableOfContents = [],
    bylineType = 'standard',
  } = props;

  const articleCanonicalUrl = article?.url?.canonical;
  const [boxflexRendered, setBoxflexRendered] = useState(false);
  const saveHistory = useMyNewsStore((state) => state.saveHistory);
  const savedContentId = useMyNewsStore((state) => state.savedContentId);
  const authenticationState = getAuthenticationState();
  const featureFlags = useFeatureFlagContext();
  const { isSorM: isMobileOrTablet = false } = useContext(BreakpointContext);

  const isAuthenticated = authenticationState === 'authenticated';
  const openAuthenticationModal = launchAuthenticationModal();


  useEffect(() => {
    if (article) {
      const articleBody = getNormalizedArticleBody(article);
      const embeddedProducts = articleBody
        .filter(({ type }) => (type === 'embeddedProduct'))
        .map((embeddedProduct) => embeddedProduct.product);

      // eslint-disable-next-line max-len
      const amazonProductOffersTrackingObject = getAmazonProductOffersTrackingObject(embeddedProducts);

      if (amazonProductOffersTrackingObject) {
        $t('track', 'rmn_amazon_products_known', amazonProductOffersTrackingObject);
      }
    }
  }, []);

  const { ref: tableOfContentsAnchorRef, inView, entry } = useInView();

  const isAboveViewport = !inView && entry?.boundingClientRect.top < 0;

  // Change/update table of contents visibility based on intersection
  useEffect(() => {
    if (setIsInlineTableOfContentsInView) {
      setIsInlineTableOfContentsInView(!isAboveViewport);
    }
  }, [isAboveViewport, setIsInlineTableOfContentsInView]);

  const ads = [];
  const {
    isScrolledPastFirstWindow,
    isScrollingUp,
    isInView,
  } = useArticleScroll({
    article,
    ads,
  });

  useSaveArticleHistory({
    authenticationState,
    savedContentId,
    article,
    saveHistory,
    articleId: article?.id,
    featureFlags,
    pageType: 'article',
  });

  const articleHasEmbeddedTaxonomy = useMemo(() => article?.body.find((item) => item.type === 'embeddedTaxonomy') || null, [article?.body]);

  if (!article) {
    return null;
  }

  const {
    ecommerceEnabled,
    headline,
    source,
    taxonomy,
    url,
    breakingNews,
    breakingTagDisabled,
    primaryImage,
    primaryMedia,
    adsEnabled,
    nativeAd,
    authors,
  } = article;

  const renderLiveBlogPost = isLiveBlog && articleHasEmbeddedTaxonomy;
  const showBoxBestsellersBacon = shouldShowBoxesBestsellersBacon(
    vertical,
    article,
    boxBestsellersList,
  );

  /**
   * Gets the developing story tag for the article.
   * @returns {React.ReactNode} The developing story tag.
   */
  const getDevelopingStoryTag = () => {
    if (!breakingNews || isLiveBlog || breakingTagDisabled) {
      return null;
    }

    return (
      <div
        className="articleDevelopingTag"
        data-testid="article-developing-tag"
      >
        <div className="articleDevelopingTag__stripe-border">
          <span className="articleDevelopingTag__stripe-border--solid db" />
        </div>
        <div className="articleDevelopingTag__content">
          <Trans i18nKey="developingStory">
            This is a
            <span className="articleDevelopingTag__content--red"> developing </span>
            story. Please check back for updates.
          </Trans>
        </div>
      </div>
    );
  };


  /**
     * Gets the expanded byline for the article.
     * @returns {React.ReactNode} The expanded byline.
     */
  const getExpandedByline = () => {
    if (isShellArticle(article)) {
      return null;
    }

    return (
      <ExpandedBylineContributors
        article={article}
        vertical={vertical}
        additionalClasses="articleBylineContainer"
        bylineAdditionalClasses="article-expanded-byline"
        data-test="article-byline"
      />
    );
  };

  /**
     * Gets the CSS classes for the body container.
     * @returns {object} The CSS classes.
     */
  const getBodyContainerClasses = () => {
    const conditionalClasses = {
      'article-body__grid--container-no-main-image': !primaryImage && !primaryMedia,
      'article-body__ecommerce-enabled': ecommerceEnabled,
      'article-body__grid--container-live-blog': isLiveBlog,
    };

    const hasArticleSectionCounter = getFeatureConfigForBrand(
      ARTICLE_TAXONOMY_SECTION_NUMBERING,
      vertical,
    );

    if (Array.isArray(hasArticleSectionCounter)) {
      const termPaths = extractTermPaths(taxonomy);
      const hasMatchingClass = hasArticleSectionCounter.filter((tax) => termPaths.includes(tax));
      conditionalClasses['article-body__section-counter'] = hasMatchingClass.length > 0;
    }

    return conditionalClasses;
  };

  /**
   * Determines if the Today Gift Guide should be rendered.
   * @returns {boolean} True if the Today Gift Guide should be rendered, false otherwise.
   */
  const shouldRenderTodayGiftGuide = () => {
    const subTypeIsNotLiveBlogOrBlog = !isBlogArticle(article);
    const showReaderFavoritesBacon = vertical === TODAY
      && shouldShowProductBacon(article, featureFlags);

    // don't show three bacons on mobile
    if (isMobileOrTablet && showReaderFavoritesBacon && showBoxBestsellersBacon) {
      return false;
    }

    return ecommerceEnabled && vertical === TODAY && subTypeIsNotLiveBlogOrBlog;
  };

  /**
   * Registers an ad element.
   * @param {HTMLElement} el - The element to register.
   */
  const registerAd = (el) => {
    const ad = el && el.querySelector('.article-body__right-rail--ad');
    if (ad) {
      ads.current = [...ads, ad];
    }
  };

  /**
   * Renders the interstitial ad if applicable.
   * @returns {React.ReactNode|null} The interstitial ad component or null.
   */
  const interstitialAd = () => {
    if (shouldShowAd(article) && [NEWS, TODAY].includes(vertical)) {
      return (
        <Ad
          slot="interstitial"
          refreshInterval={0}
        />
      );
    }

    return null;
  };

  /**
 * Renders the right rail section of the article.
 * @param {object} params - The parameters.
 * @param {object} params.section - The section object.
 * @param {number} params.sectionIndex - The index of the section.
 * @param {boolean} params.articleContainsLargeProduct - Indicates if the article contains a large product.
 * @returns {React.ReactNode} The right rail section.
 */
  const renderRightRail = ({
    section, sectionIndex, articleContainsLargeProduct,
  }) => (
    <div
      className={`${block}--right-rail-container`}
      ref={(el) => registerAd(el)}
    >
      <BodyRightRail
        block={block}
        section={section}
        sectionIndex={sectionIndex}
        articleContainsLargeProduct={articleContainsLargeProduct}
        shouldRenderRightRailTaboola={shouldRenderRightRailTaboola}
        getRightRailAdConfig={getRightRailAdConfig}
        currentPath={currentPath}
        boxflexRendered={boxflexRendered}
        isScrollingUp={isScrollingUp}
        showTaboola={isInView}
        boxflexAdRenderCheck={(mpsAd) => boxflexAdRenderCheck({
          mpsAd,
          setBoxflexRendered,
        })}
        tableOfContents={tableOfContents}
      />
    </div>
  );

  const isNotShellArticle = !isShellArticle(article);

  const hasSocialsBelowArticle = showUserActions
    && getFeatureConfigForBrand(USER_ACTIONS_BELOW_ARTICLE, vertical);
  const hasSocialMenuBelowArticle = isNotShellArticle
    && hasSocialsBelowArticle;

  const articleBody = getNormalizedArticleBody(article);
  const articleContainsLargeProduct = isShoppable || !!articleBody.find(
    ({ type, presentation }) => (type === 'embeddedProduct' && presentation.size === 'large'),
  );

  const articleContainsUniCheckout = !!articleBody.find(
    ({ type }) => (type === 'embeddedProduct'),
  );
  const isPrimarySectionFood = (article?.taxonomy?.primarySection?.name ?? '').toLowerCase() === 'food';

  const embeddedRecipes = articleBody.filter((item) => item.type === 'embeddedRecipe');
  const shouldShowFeaturedRecipes = embeddedRecipes && embeddedRecipes.length > 1
    && isPrimarySectionFood;

  const sections = getSections({
    adsEnabled,
    article,
    articleBody,
    breakingNews,
    ecommerceEnabled,
    isShoppable,
    isLiveBlog,
    nativeAd,
    path,
    taboolaRecoReelEnabled,
    vertical,
    gateAccess,
    featureFlags,
    isChromeless,
  });

  const sectionsIndex = sections.map((section, index) => ({ index, ...section }));
  const sectionLast = sections.length - 1;

  const gridContainerClasses = classNames(
    'article-body__grid--container',
    getBodyContainerClasses(),
    {
      'article_body__grid--shell-container': isShellArticle(article),
      // class is targeted in shell article pages
      'article-body__gridContainer': isShellArticle(article),
    },
  );

  const gridBodyClasses = classNames(
    { 'layout-grid-item layout-grid-item--with-gutter-s-only grid-col-10-m grid-col-push-1-m grid-col-6-xl grid-col-push-2-xl': !disableGrid },
    { 'article-body--custom-column': !isShellArticle(article) && !disableGrid },
  );

  const bodyGridClasses = classNames(
    'article-body',
    gridBodyClasses,
  );
  const bodyFullWidthClasses = 'article-body layout-grid-item';

  const dataTaboolaTarget = !isChromeless && !isLiveBlog && gateAccess && shouldRenderTaboolaFeed ? 'read-more' : '';

  const shouldRenderBackToTopButton = isScrolledPastFirstWindow
    && !isLiveBlog
    && ecommerceEnabled;

  const dataActivityMapID = getDataActivityMapID({
    pageRegion: 'article-body',
    componentName: 'article',
  });

  const isNews = vertical === NEWS;
  const userActions = getFeatureConfigForBrand(SHOW_USER_ACTIONS, vertical);
  const { showPinterest } = userActions || {};

  const isShell = article.presentation.style === 'shell';
  const isStartToday = view === VIEW.START_TODAY_APP;

  const showMostPopular = isNews
    && !breakingNews
    && !isChromeless
    && !nativeAd
    && !isShell
    && !ecommerceEnabled;

  const shouldRenderRelated = isNotShellArticle
    && relatedArticles?.length >= 3
    && !isStartToday
    && !isChromeless;

  const showStandardByline = bylineType === 'standard';

  /**
   * Renders the bylines for the article.
   * @returns {React.ReactNode} The rendered bylines.
   */
  const renderBylines = () => (
    <>
      <StandardByline
        {...{
          article,
          block,
          bylineTimestampDatePublished,
          contentId,
          isChromeless,
          isLiveBlog,
          showBylineTimestamp: showBylineTimestamp && showStandardByline,
          showCreatedDate,
          showInlineByline: showInlineByline && showStandardByline,
          showUserActions,
          tableOfContentsEnabled,
          vertical,
        }}
      />
      {bylineType === 'expandable' && (
        <ExpandableByline
          authors={authors}
          createdAt={article?.date?.createdAt}
          className="article-body__expandable-byline"
          orgName={article?.source?.organization?.name}
          orgUrl={article?.source?.organization?.externalUrl}
          publishedAt={article?.date?.publishedAt}
          source={article?.source}
        />
      )}
    </>
  );
  const shouldShowDecibelPlayer = (showDecibelPlayer && !isChromeless) || getCookie('forceShowDecibelPlayer') === 'true';

  const hasAudioFile = !!article?.audioNarration?.audio?.audioAssets?.[0]?.playbackUrl;
  const baseTrackingData = {
    consumer: 'Ramen',
    trackingVariant: 'article',
    segment: article?.audioNarration?.audio?.audioAssets?.[0]?.playbackUrl || '',
    guid: `audio-${article.id}`,
    date: new Date(),
  };

  /**
     * Renders the article body sections.
     * @returns {React.ReactNode} The rendered article body sections.
     */
  const renderArticleBody = () => (
    sectionsIndex.map((section, i) => (
      <div
        key={section.index}
        className={classNames(
          { 'article-body__section layout-grid-container': (!disableGrid && !section.isFullWidth) },
          {
            'article-body__last-section': i === sections.length - 1,
            'article-body__first-section': i === 0,
          },
        )}
      >
        {articleContainsUniCheckout
          && getFeatureConfigForBrand(UNIVERSAL_CHECKOUT_WELLS_FARGO_BANNER, vertical)
          && <WellsFargoBanner bodyClasses={bodyGridClasses} />}
        <div
          className={section.isFullWidth ? bodyFullWidthClasses : bodyGridClasses}
          data-test="articleBody"
        >
          {i === 0 && renderBylines()}
          {shouldShowFeaturedRecipes
            ? <FeaturedRecipes embeddedRecipes={embeddedRecipes} /> : null}

          {i === 0 && shouldShowDecibelPlayer && !isChromeless && hasAudioFile
            && (
              <Decibel
                env={process.env.NODE_ENV}
                rum={datadogRum}
                disableControls={!isAuthenticated}
                disabledControlsCallback={openAuthenticationModal}
                $t={$t}
                baseTrackingData={baseTrackingData}
                file={article?.audioNarration?.audio?.audioAssets?.[0]?.playbackUrl}
                offset={60}
                font="founders-cond"
                callToAction={isAuthenticated ? 'Listen to this article'
                  : (
                    <div>
                      <span>Listen to this article with a </span>
                      <span className="db:text-[#3061ff] db:cursor-pointer">
                        free account
                      </span>
                    </div>
                  )}
              />
            )}

          {/* When the TableOfContents scrolls off the page, activate the sticky */}
          {i === 0 && isMobileOrTablet && tableOfContents.isEnabled && (
            <>
              <TableOfContents
                isCollapsible={false}
                links={tableOfContents.links}
                title={tableOfContents.title}
                variant="inline"
              />
              <div data-testid="table-of-contents-anchor-wrapper" ref={tableOfContentsAnchorRef} style={{ height: 0, overflow: 'hidden' }}>
                <div data-testid="table-of-contents-anchor" style={{ height: 1 }} />
              </div>
            </>
          )}
          <div className="article-body__content">
            {section.items}
          </div>
          {i === sectionLast
            && isLiveBlog
            && hasSocialMenuBelowArticle && (
              <SocialShareMenu
                url={url.primary}
                headline={headline.tease}
                contentId={contentId}
                trackingEventName="article_social_share_bottom"
                actions={{ pinterest: showPinterest }}
              />
            )}
          <div className="article-body__content">
            {i === sectionLast
              && (breakingNews || isLiveBlog)
              && (
                <>
                  {renderLiveBlogPost && (
                    <ErrorBoundary errorLogger={logError}>
                      <LiveBlogPosts
                        articleCanonicalUrl={articleCanonicalUrl}
                        backToTopBtnsAlignDesktop={backToTopBtnsAlignDesktop}
                        backToTopBtnsAlignMobile={backToTopBtnsAlignMobile}
                      />
                    </ErrorBoundary>
                  )}
                  {getDevelopingStoryTag()}
                  {getFeatureConfigForBrand(ARTICLE_INLINE_NEWSLETTER, vertical)
                    && vertical !== 'today'
                    && (
                      <NewsletterSignupInline
                        article={article}
                        vertical={vertical}
                        isChromeless={isChromeless}
                      />
                    )}
                </>
              )}
            {i === sectionLast && (
              <>
                {getExpandedByline()}
                {hasSocialMenuBelowArticle && !isLiveBlog && !isStartToday ? (
                  <div className="article-social-share-bottom">
                    <SocialShareMenu
                      url={url.primary}
                      headline={headline.tease}
                      contentId={contentId}
                      pageRegion="article-bottom"
                      trackingEventName="article_social_share_bottom"
                      actions={{ pinterest: showPinterest }}
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>

        </div>
        {renderRightRail({
          section, sectionIndex: i, articleContainsLargeProduct,
        })}
      </div>
    ))
  );


  /**
     * Renders the gated article body.
     * @returns {React.ReactElement} The gated article body.
     */
  const renderGatedArticleBody = () => {
    const section = sectionsIndex[0];

    return (
      <div
        className="article-body__gate article-body__first-section article-body__section layout-grid-container"
        data-testid="gated-article-body"
      >
        <div
          data-test="articleBody"
          className={bodyGridClasses}
        >
          {renderBylines()}
          <div
            className={
              classNames(
                'article-body__content',
                'article-body__content--gated',
              )
            }
          >
            {/* Render section components */}
            {section.items}
          </div>
          <AccountLoginRegistration layout="fluid" contentType="article" className="gated-content--wrapper" />
        </div>
        {renderRightRail({
          section: sections[0], sectionIndex: 0, articleContainsLargeProduct,
        })}
      </div>
    );
  };

  return (
    <>
      <div
        className={classNames(block, className, {
          breaking: breakingNews,
          'is-live-blog': isLiveBlog,
        })}
        data-activity-map={dataActivityMapID}
        data-taboola-target={dataTaboolaTarget}
        data-testid="article-body"
      >
        {shouldRenderBackToTopButton && <BackToTopButton />}
        <div className={gridContainerClasses}>
          {interstitialAd()}
          <SocialAndSponsored
            {...{
              article,
              bodyClasses: bodyGridClasses,
              nativeAd,
              source,
            }}
          />
          {gateAccess ? renderArticleBody() : renderGatedArticleBody()}

          {!isShoppable && (
            <>
              <RecirculationBacon pageRegion="article-bottom" />
              {showBoxBestsellersBacon && (
                <RecirculationBacon
                  pageRegion="article-bottom"
                  curatedList={boxBestsellersList}
                />
              )}
            </>
          )}

          <BodyBottomRecommended
            block={block}
            currentPath={path}
            gridBodyClasses={gridBodyClasses}
            shouldRenderTodayGiftGuide={shouldRenderTodayGiftGuide()}
            showTaboola={isInView}
          />
        </div>
        {showMostPopular && (
          <MostPopularStoryList
            mostPopularStoryListItems={mostPopularStoryListItems}
          />
        )}
      </div>

      <ArticleFoot vertical={vertical} currentPath={path} article={article} />

      {shouldRenderTaboolaFeed && (
        <BodyTaboola article={article} showTaboola={isInView} />
      )}

      {shouldRenderRelated ? (
        <Related
          article={article}
          relatedArticles={relatedArticles}
          vertical={vertical}
          view
        />
      ) : null}
    </>
  );
}

export default connect(mapStateToProps)(
  withAccountWorkflows(withGateAccess(ArticleBody)),
  useMyNewsStore,
);
